import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import Seo from "@/components/Seo";
import { api } from "@/lib/api";

const THEMES = [
  { key: "consistency", label: "Consistency" },
  { key: "strength",    label: "Strength" },
  { key: "confidence",  label: "Confidence" },
  { key: "community",   label: "Community" },
];

export default function Results() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    api.get("/reviews/google")
      .then((r) => setReviews((r.data?.reviews || []).slice(0, 12)))
      .catch(() => setReviews([]));
  }, []);

  return (
    <div className="bg-kore-warm">
      <Seo
        title="Results — real KORE members, real stories"
        description="Real people, real training weeks. Members in their 40s, 50s, 60s and beyond talk about consistency, strength, confidence and community at KORE Wellbeing."
        path="/results"
      />

      {/* Hero */}
      <section className="bg-kore-forest text-kore-warm pt-32 pb-20 md:pt-40 md:pb-24">
        <div className="container-kore max-w-4xl">
          <span className="text-[11px] uppercase tracking-[0.28em] text-kore-light">Real member stories</span>
          <h1 className="font-display font-light text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-[-0.02em] mt-6">
            Real people.<br/>
            <span className="italic text-kore-light">Real training weeks.</span>
          </h1>
          <p className="mt-8 text-lg text-kore-warm/85 leading-relaxed max-w-2xl">
            No dramatic before-and-afters. Just members in their 40s, 50s, 60s and beyond who've finally found a gym they can stick with — and become stronger, fitter and more consistent along the way.
          </p>
        </div>
      </section>

      {/* Theme labels */}
      <section className="bg-kore-warm border-b border-kore-gold/15 py-10">
        <div className="container-kore flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-center">
          <span className="text-[11px] uppercase tracking-[0.24em] text-kore-ink/60">What members tell us</span>
          {THEMES.map((t) => (
            <span key={t.key} className="text-[11px] uppercase tracking-[0.24em] text-kore-forest/85">{t.label}</span>
          ))}
        </div>
      </section>

      {/* Reviews grid */}
      <section className="py-24 md:py-32">
        <div className="container-kore">
          {reviews.length === 0 ? (
            <p className="text-kore-ink/50 text-center">Loading member stories…</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {reviews.map((r, i) => (
                <figure key={r.name + i} className="rounded-3xl bg-white border border-kore-gold/15 p-8 shadow-soft flex flex-col">
                  <div className="flex gap-0.5">
                    {Array.from({ length: r.rating || 5 }).map((_, s) => (
                      <Star key={s} size={13} className="text-kore-gold fill-kore-gold" />
                    ))}
                  </div>
                  <blockquote className="mt-5 text-kore-ink/85 leading-relaxed text-[15px] flex-1">
                    &ldquo;{r.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-6 pt-4 border-t border-kore-gold/15 flex items-center gap-3">
                    {r.photo ? (
                      <img src={r.photo} alt={r.name} referrerPolicy="no-referrer" loading="lazy" className="w-10 h-10 rounded-full object-cover border border-kore-gold/20" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-kore-forest text-kore-warm flex items-center justify-center text-xs font-medium">
                        {(r.name || "K").slice(0, 1)}
                      </div>
                    )}
                    <div>
                      <div className="font-display text-kore-forest">{r.name}</div>
                      <div className="text-[11px] uppercase tracking-[0.2em] text-kore-ink/50 mt-0.5">
                        Google review{r.relative_time ? ` · ${r.relative_time}` : ""}
                      </div>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-kore-forest text-kore-warm py-28 md:py-36 text-center">
        <div className="container-kore max-w-2xl">
          <h2 className="display-2">Your turn.</h2>
          <p className="mt-6 text-kore-warm/80 leading-relaxed">
            Start with the 30 Day Coached Trial. Get coached, meet the community and see for yourself.
          </p>
          <Link to="/30-day-trial" data-testid="results-cta" className="btn-gold mt-10">
            Start your 30 Day Trial <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
}
